import React, { useState, useEffect, useRef, useCallback, useImperativeHandle } from "react";
import { useTranslation } from "react-i18next";
import { Box } from "grommet";
import HotelService from "../../services/hotel.service";
import CheckinFlowService from "../../services/checkin-flow.service";
import { presentToastSuccess, presentToastErrorContent } from "@shared/Toast";
import { normalizeCommsTagSpacing } from "@features/shared/normalizeTagSpacing";
import { useFetchHotel } from "./redux/hooks";
import CheckinFlowPreCheckin from "./components/CheckinFlowPreCheckin";
import CheckinFlowReminder from "./components/CheckinFlowReminder";
import CheckinFlowApproval from "./components/CheckinFlowApproval";
import CheckinFlowAccess from "./components/CheckinFlowAccess";
import CheckinFlowPostCheckin from "./components/CheckinFlowPostCheckin";
import CheckinFlowTimeline from "./components/CheckinFlowTimeline";
import OtaEmailNotice from "./components/OtaEmailNotice";
import CheckinFlowDescription from "./components/CheckinFlowDescription";
import MessagingCheckinFlow from "./components/MessagingCheckinFlow";
import "./SettingsCheckinFlow.scss";

import { getOfferCapabilities, LOCK_FIELDS, UPSELL_FIELDS, changedVisibleFields } from "../../common/offerCapabilities";

const HOTEL_FIELDS = {
  onlineCheckinEnabled: false,
  requestPhotoID: false,
  aiPreCheckEnabled: false,
  requestCardImprint: false,
  offerEarlyCheckin: false,
  expressCheckout: true,
  deposit: 200,
  currency: "usd",
  registrationEnabled: false,
  registrationTextEn: "",
  registrationTextFr: "",
  checkinStepsRequired: false,
  webrezproAgreement: false,
  autoSendEnabled: false,
  autoSendWalkinTime: "",
  autoSendValidNow: false,
  useFallbackOfflineCodes: false,
  useRandomCode: false,
  sendKeysOnlyIfApproved: false,
  onlyIfWRPAgreementCompletedWalkin: false,
  postCheckinMessageEnabled: false,
  checkinApproval: { autoApprove: "never", getNotified: "always" },
  reminderEnabled: false,
  OTAPrecheckinPolicy: "all",
};

const SECTION_IDS = [
  "precheckin",
  "reminder",
  "approval",
  "access",
  "postcheckin",
];

function CheckinFlowEditor({ automationManaged = false, automationKind = "precheckin", channels, onDraftState, settingsRef }) {
  const { t } = useTranslation();
  const { hotel, fetchHotel } = useFetchHotel();
  const caps = getOfferCapabilities(hotel);
  const [formValues, setFormValues] = useState(HOTEL_FIELDS);
  const [communications, setCommunications] = useState({});
  const [savedCommunications, setSavedCommunications] = useState({});
  const [commDefaults, setCommDefaults] = useState({});
  const [rawDefaults, setRawDefaults] = useState({});
  const [activeSection, setActiveSection] = useState("precheckin");
  const scrollContainerRef = useRef(null);
  const [loaded, setLoaded] = useState(false);
  const [loadError, setLoadError] = useState(false);
  const baseline = useRef(HOTEL_FIELDS);
  const managedKeys = automationKind === "reminder"
    ? ["reminderUseCheckinEmail", "reminderEmail", "reminderText", "reminderEmailUpsells"]
    : ["useCustomCheckinEmail", "checkinEmail", "useCustomCheckinText", "checkinText", "checkinEmailUpsells"];
  const managedData = Object.fromEntries(managedKeys.filter(key => communications[key] !== undefined).map(key => [key, communications[key]]));
  const formDirty = JSON.stringify(formValues) !== JSON.stringify(baseline.current);
  const commDirty = managedKeys.some(key => JSON.stringify(communications[key]) !== JSON.stringify(savedCommunications[key]));
  const usesParent = automationKind === "reminder" && communications.reminderUseCheckinEmail !== false;
  const emailKey = automationKind === "reminder" && !usesParent ? "reminderEmail" : "checkinEmail";
  const textKey = automationKind === "reminder" && !usesParent ? "reminderText" : "checkinText";
  const customEmail = emailKey === "reminderEmail" || communications.useCustomCheckinEmail;
  const customText = textKey === "reminderText" || communications.useCustomCheckinText;
  const email = (customEmail ? communications[emailKey] : null) || commDefaults[emailKey];
  const text = (customText ? communications[textKey] : null) || commDefaults[textKey];
  const languages = hotel?.language === "fr" ? ["en", "fr"] : ["en"];
  const contains = (value, tag) => String(value || "").includes(`{${tag}}`) || String(value || "").includes(`data-tag="${tag}"`);
  const invalid = loaded && languages.some(lang =>
    (channels?.includes("email") && customEmail && (!email?.body?.[lang]?.replace(/<[^>]*>/g, "").trim() || !contains(email.body[lang], "precheckinButton"))) ||
    (channels?.includes("sms") && customText && (!text?.[lang]?.trim() || !contains(text[lang], "magicLink") || text[lang].length > 200)));
  const bodiesKey = JSON.stringify(customEmail ? email?.body || {} : {});
  useEffect(() => { onDraftState?.({ ready: loaded && !loadError, dirty: formDirty || commDirty, invalid, bodies: JSON.parse(bodiesKey) }); }, [onDraftState, loaded, loadError, formDirty, commDirty, invalid, bodiesKey]);
  useImperativeHandle(settingsRef, () => ({ save: async () => {
    if (!loaded || loadError || invalid) throw new Error("Review the email and text content before saving.");
    const hidden = [...LOCK_FIELDS, ...(!caps.upsells ? UPSELL_FIELDS : []), "onlineCheckinEnabled", "reminderEnabled"];
    const update = automationKind === "precheckin" ? changedVisibleFields(formValues, baseline.current, Object.keys(HOTEL_FIELDS), hidden) : {};
    if (Object.keys(update).length) await HotelService.updateHotel(update);
    const data = normalizeCommsTagSpacing(managedData);
    if (!caps.upsells) Object.keys(data).filter(key => /Upsells$/.test(key)).forEach(key => delete data[key]);
    if (commDirty) await CheckinFlowService.updateCommunications(data);
    baseline.current = formValues;
    setSavedCommunications(communications);
  } }));

  // Auto-save hotel fields
  const handleFormChange = useCallback(
    async (newValues) => {
      setFormValues(newValues);
      if (automationManaged) return;

      // Build only the changed fields
      const caps = getOfferCapabilities(hotel);
      const hidden = [...(!caps.accessDoors ? LOCK_FIELDS : []), ...(!caps.upsells ? UPSELL_FIELDS : [])];
      const hotelUpdate = changedVisibleFields(newValues, formValues, Object.keys(HOTEL_FIELDS), hidden);

      const forceFlags = automationManaged ? {} : {
        ...(hotelUpdate.onlineCheckinEnabled === false &&
        hotel?.pmsSlug === "webrezpro"
          ? { sendKeysOnlyIfApproved: false }
          : {}),
        ...(hotelUpdate.onlineCheckinEnabled
          ? { onlyIfWRPAgreementCompletedWalkin: false, reminderEnabled: true }
          : {}),
        ...(hotelUpdate.onlineCheckinEnabled === false
          ? { reminderEnabled: false }
          : {}),
      };

      try {
        const update = { ...hotelUpdate, ...forceFlags };
        if (automationManaged) {
          delete update.onlineCheckinEnabled;
          delete update.reminderEnabled;
        }
        hidden.forEach(key => delete update[key]);
        await HotelService.updateHotel(update);
        fetchHotel();
      } catch (error) {
        presentToastErrorContent(error);
      }
    },
    [fetchHotel, hotel, formValues, automationManaged],
  );

  // Save communications on demand (for email/text editors)
  const saveCommunications = useCallback(
    async (commsOverride) => {
      if (automationManaged) { if (commsOverride) setCommunications(commsOverride); return; }
      const data = normalizeCommsTagSpacing(commsOverride || communications);
      Object.keys(data).forEach(key => {
        if ((!caps.accessDoors && /access|postCheckin/i.test(key)) || (!caps.upsells && /Upsells$/.test(key))) delete data[key];
      });
      try {
        await CheckinFlowService.updateCommunications(data);
        if (commsOverride) setCommunications(data);
        setSavedCommunications(data);
        presentToastSuccess("Saved");
      } catch (error) {
        presentToastErrorContent(error);
      }
    },
    [communications, caps.accessDoors, caps.upsells, automationManaged],
  );

  // Load hotel data
  useEffect(() => {
    const hotelValues = { ...HOTEL_FIELDS };
    for (const key of Object.keys(HOTEL_FIELDS)) {
      if (hotel[key] !== undefined) {
        hotelValues[key] = hotel[key];
      }
    }
    baseline.current = hotelValues;
    setFormValues(hotelValues);
  }, [hotel]);

  // Replace {hotel} and {pronounsFr.*} tags in defaults so the preview the
  // user sees in "revert to default" matches what will actually be sent.
  useEffect(() => {
    if (!rawDefaults || !Object.keys(rawDefaults).length) return;
    const hotelName = hotel?.name || "";
    const pronouns = (() => {
      switch (hotel?.pronounsFr) {
        case "LA":
          return { le: "la ", au: "à la ", du: "de la " };
        case "LES":
          return { le: "les ", au: "aux ", du: "des " };
        case "L":
          return { le: "l'", au: "à l'", du: "de l'" };
        default:
          return { le: "le ", au: "au ", du: "du " };
      }
    })();
    let json = JSON.stringify(rawDefaults).replace(/\{hotel\}/g, hotelName);
    for (const [k, v] of Object.entries(pronouns)) {
      json = json.replace(new RegExp(`\\{pronounsFr\\.${k}\\}`, "g"), v);
    }
    const replaced = JSON.parse(json);
    setCommDefaults(replaced);
  }, [rawDefaults, hotel?.name, hotel?.pronounsFr]);

  // Load communications
  useEffect(() => {
    CheckinFlowService.getCommunications().then(
      (res) => {
        const { defaults, ...comms } = res.data;
        setCommunications(comms);
        setSavedCommunications(comms);
        if (defaults) setRawDefaults(defaults);
        setLoaded(true);
      },
      () => { setLoadError(true); },
    );
  }, []);

  // Scroll spy for timeline highlight
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (entry.isIntersecting) {
            const id = entry.target.id.replace("section-", "");
            setActiveSection(id);
          }
        }
      },
      { rootMargin: "-20% 0px -60% 0px" },
    );

    SECTION_IDS.forEach((id) => {
      const el = document.getElementById(`section-${id}`);
      if (el) observer.observe(el);
    });

    return () => observer.disconnect();
  }, []);

  const timelineConfig = {
    ...formValues,
    communications,
    offerEarlyCheckin: caps.upsells && formValues.offerEarlyCheckin,
    lockType: hotel?.lockType,
    serviceType: hotel?.serviceType,
  };

  return (
    <div className="settings-checkin-flow">
      <div className="settings-checkin-flow__layout">
        {/* Left: Configuration Form */}
        <div className="settings-checkin-flow__form" ref={scrollContainerRef}>
          {!automationManaged && <CheckinFlowTimeline
            config={timelineConfig}
            activeSection={activeSection}
            horizontal
          />}
          {!automationManaged && caps.accessDoors && <OtaEmailNotice hotel={hotel} />}
          {loadError && <p role="alert">{t("automation.settingsLoadError")}</p>}
          <Box gap="large">
            {/* T-48h: Online Check-in */}
            {(!automationManaged || automationKind === "precheckin") && <Box className={automationManaged ? "automation-special-content" : undefined} background={automationManaged ? undefined : "light-2"} round="small" pad={automationManaged ? undefined : "medium"}>
              <CheckinFlowPreCheckin
                formValues={formValues}
                onChange={handleFormChange}
                communications={communications}
                onCommunicationsChange={setCommunications}
                onSaveCommunications={saveCommunications}
                savedCommunications={savedCommunications}
                hotel={hotel}
                defaults={commDefaults}
                automationManaged={automationManaged}
                channels={channels}
              />
            </Box>}

            {/* T-24h: Reminder */}
            {(automationManaged ? automationKind === "reminder" : formValues.onlineCheckinEnabled) && (
              <Box className={automationManaged ? "automation-special-content" : undefined} background={automationManaged ? undefined : "light-2"} round="small" pad={automationManaged ? undefined : "medium"}>
                <CheckinFlowReminder
                  communications={communications}
                  onCommunicationsChange={setCommunications}
                  onSaveCommunications={saveCommunications}
                  savedCommunications={savedCommunications}
                  hotel={hotel}
                  defaults={commDefaults}
                  automationManaged={automationManaged}
                  channels={channels}
                  onHotelChange={(partial) =>
                    handleFormChange({ ...formValues, ...partial })
                  }
                />
              </Box>
            )}

            {/* Check-in Approval */}
            {(automationManaged ? automationKind === "precheckin" : formValues.onlineCheckinEnabled) && (
              <Box className={automationManaged ? "automation-editor-section automation-special-section" : undefined} background={automationManaged ? undefined : "light-2"} round="small" pad={automationManaged ? undefined : "medium"}>
                <CheckinFlowApproval
                  formValues={formValues}
                  onChange={handleFormChange}
                  hotel={hotel}
                />
              </Box>
            )}

            {/* Access Instructions */}
            {caps.accessDoors && <Box className={automationManaged ? "automation-editor-section automation-special-section" : undefined} background={automationManaged ? undefined : "light-2"} round="small" pad={automationManaged ? undefined : "medium"}>
              <CheckinFlowAccess
                formValues={formValues}
                onChange={handleFormChange}
                communications={communications}
                onCommunicationsChange={setCommunications}
                onSaveCommunications={saveCommunications}
                savedCommunications={savedCommunications}
                isOnlineCheckinEnabled={!!formValues.onlineCheckinEnabled}
                hotel={hotel}
                defaults={commDefaults}
              />
            </Box>}

            {/* Post Check-in */}
            {caps.accessDoors && hotel?.lockType !== "oracode" && (
              <Box className={automationManaged ? "automation-editor-section automation-special-section" : undefined} background={automationManaged ? undefined : "light-2"} round="small" pad={automationManaged ? undefined : "medium"}>
                <CheckinFlowPostCheckin
                  formValues={formValues}
                  onChange={handleFormChange}
                  communications={communications}
                  onCommunicationsChange={setCommunications}
                  onSaveCommunications={saveCommunications}
                  savedCommunications={savedCommunications}
                  defaults={commDefaults}
                  hotel={hotel}
                />
              </Box>
            )}
          </Box>
        </div>

        {/* Right: Description */}
        {!automationManaged && <div className="settings-checkin-flow__sidebar">
          <CheckinFlowDescription
            hotel={hotel}
            communications={communications}
          />
        </div>}
      </div>
    </div>
  );
}

export default function SettingsCheckinFlow() {
  const { hotel, fetchHotel } = useFetchHotel();
  if (hotel?.serviceType === "messaging") {
    return <MessagingCheckinFlow key={hotel._id} hotel={hotel} fetchHotel={fetchHotel}>
      <CheckinFlowEditor automationManaged />
    </MessagingCheckinFlow>;
  }
  return <CheckinFlowEditor />;
}
